/**
 * Visualizzatore messaggio + thread con azioni complete:
 * Reply, Smart Reply, Forward, Archive, Trash, Snooze, Star, Allegati.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
  Reply, Forward, Star, Trash2, Sparkles, Loader2, Archive, Ban,
  Paperclip, Download, Bell, Clock as ClockIcon, X, ChevronDown, Tag, Check,
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { messagesApi, threadsApi, aiApi, attachmentsApi, remindersApi, draftsApi, labelsApi, spamApi, type AttachmentItem } from '@/lib/api/endpoints';
import { AvvisoMessaggio } from '@/components/spam/AvvisoMessaggio';
import { useComposer } from '@/stores/composer.store';
import { SnoozePicker } from './SnoozePicker';
import { ApiError, API_URL, tokenStore } from '@/lib/api/client';
import { toast } from 'sonner';

// I link nelle email si aprono in una nuova scheda, isolati dalla webmail.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

// Tag sempre vietati: vettori XSS o esfiltrazione. DOMPurify rimuove già da sé
// tutti gli handler inline (onerror, onload, onclick, ...) e i tag <script>.
const SANITIZE_CONFIG = {
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'base', 'meta', 'link'],
  ALLOW_DATA_ATTR: false,
};

export interface MessageViewerProps {
  messageId: string;
  accountId: string;
  onClose?: () => void;
}

export function MessageViewer({ messageId, accountId: _accountId, onClose }: MessageViewerProps) {
  const qc = useQueryClient();
  const openCompose = useComposer((s) => s.openCompose);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['message', messageId],
    queryFn: () => messagesApi.detail(messageId),
  });
  const message = data?.message;
  const security = data?.security ?? null;
  const threadId = message?.thread_id;

  const { data: threadData } = useQuery({
    queryKey: ['thread', threadId],
    queryFn: () => (threadId ? messagesApi.thread(threadId) : Promise.resolve(null)),
    enabled: !!threadId,
  });

  const { data: attsData } = useQuery({
    queryKey: ['attachments', messageId],
    queryFn: () => (message?.has_attachments ? attachmentsApi.list(messageId) : Promise.resolve({ attachments: [] })),
    enabled: !!message?.has_attachments,
  });

  // Mark as read on view
  useEffect(() => {
    if (message && !message.is_read) {
      messagesApi.markRead(messageId, true).then(() => {
        qc.invalidateQueries({ queryKey: ['messages'] });
        qc.invalidateQueries({ queryKey: ['message', messageId] });
      }).catch(() => { /* */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, message?.id]);

  const starMutation = useMutation({
    mutationFn: (starred: boolean) => threadsApi.star(threadId!, starred),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['thread', threadId] });
      qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => threadsApi.archive(threadId!),
    onSuccess: () => {
      toast.success('Thread archiviato');
      qc.invalidateQueries({ queryKey: ['messages'] });
      onClose?.();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore archiviazione'),
  });

  const trashMutation = useMutation({
    mutationFn: () => threadsApi.trash(threadId!),
    onSuccess: () => {
      toast.success('Thread cestinato');
      qc.invalidateQueries({ queryKey: ['messages'] });
      onClose?.();
    },
  });

  const spamMutation = useMutation({
    mutationFn: () => threadsApi.spam(threadId!),
    onSuccess: (r) => {
      toast.success(r.blocked ? `Spostato nello spam · mittente ${r.sender ?? ''} bloccato` : 'Spostato nello spam');
      qc.invalidateQueries({ queryKey: ['messages'] });
      qc.invalidateQueries({ queryKey: ['rules'] });
      onClose?.();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore'),
  });

  const snoozeMutation = useMutation({
    mutationFn: (until: string) => threadsApi.snooze(threadId!, until),
    onSuccess: () => {
      toast.success('Thread in snooze');
      qc.invalidateQueries({ queryKey: ['messages'] });
      onClose?.();
    },
  });

  const remindMutation = useMutation({
    mutationFn: (data: { remindAt: string; reason?: string }) =>
      remindersApi.create({ threadId: threadId!, ...data }),
    onSuccess: () => {
      toast.success('Promemoria impostato');
      qc.invalidateQueries({ queryKey: ['reminders'] });
    },
  });

  // Etichette: catalogo + assegnazione sul thread
  const labelsCatalogQuery = useQuery({ queryKey: ['labels'], queryFn: () => labelsApi.list() });
  const catalog = labelsCatalogQuery.data?.labels ?? [];
  const threadLabels = threadData?.thread.labels ?? [];
  const colorByName = useMemo(
    () => Object.fromEntries(catalog.map((l) => [l.name, l.color])),
    [catalog]
  );

  const setLabelsMutation = useMutation({
    mutationFn: (labels: string[]) => threadsApi.setLabels(threadId!, labels),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['thread', threadId] });
      qc.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore etichette'),
  });

  function toggleLabel(name: string) {
    const next = threadLabels.includes(name)
      ? threadLabels.filter((x) => x !== name)
      : [...threadLabels, name];
    setLabelsMutation.mutate(next);
  }

  // HTML dell'email sanitizzato prima del render (anti-XSS). Hook sempre chiamato
  // prima dei return condizionali sotto.
  const safeHtml = useMemo(
    () => (message?.body_html ? DOMPurify.sanitize(message.body_html, SANITIZE_CONFIG) : ''),
    [message?.body_html]
  );

  async function handleSummarize() {
    if (!threadId) return;
    setGeneratingSummary(true);
    try {
      await aiApi.summarizeThread(threadId);
      qc.invalidateQueries({ queryKey: ['thread', threadId] });
    } finally {
      setGeneratingSummary(false);
    }
  }

  // «L'ho controllata, è a posto»: l'avviso non torna più su questo messaggio.
  const sicuraMutation = useMutation({
    mutationFn: () => spamApi.dismissWarning(messageId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['message', messageId] }); void qc.invalidateQueries({ queryKey: ['messages'] }); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Operazione non riuscita'),
  });

  function handleReply() {
    if (!message) return;
    const subj = message.subject?.startsWith('Re:') ? message.subject : `Re: ${message.subject || ''}`;
    openCompose({
      threadId: message.thread_id,
      inReplyToMessageId: message.id,
      to: message.from_address?.email
        ? [{ name: message.from_address.name, address: message.from_address.email }]
        : [],
      subject: subj,
    });
  }

  function handleForward() {
    if (!message) return;
    const subj = message.subject?.startsWith('Fwd:') ? message.subject : `Fwd: ${message.subject || ''}`;
    const quoted = `\n\n---------- Messaggio inoltrato ----------\nDa: ${message.from_address?.email ?? ''}\nData: ${message.received_at ?? ''}\nOggetto: ${message.subject ?? ''}\n\n${message.body_text ?? ''}`;
    openCompose({
      forwardOfMessageId: message.id,
      subject: subj,
      body: quoted,
    });
  }

  function handleRemindMe() {
    const remindAt = new Date(Date.now() + 3 * 86_400_000).toISOString();
    remindMutation.mutate({ remindAt, reason: 'Nessuna risposta in 3 giorni' });
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!message) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        Messaggio non trovato
      </div>
    );
  }

  const fromName = message.from_address?.name || message.from_address?.email || '';
  const fromEmail = message.from_address?.email ?? '';
  const dateStr = message.received_at
    ? format(new Date(message.received_at), 'PPP - HH:mm', { locale: it })
    : '';
  const summary = threadData?.thread.ai_summary;
  const actionItems = threadData?.thread.ai_action_items ?? [];
  const isStarred = threadData?.thread.is_starred ?? message.is_starred;

  return (
    <motion.div
      key={message.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="flex-1 flex flex-col bg-background overflow-hidden"
    >
      {/* Toolbar */}
      <div className="border-b px-4 py-2 flex items-center gap-1 flex-wrap">
        <Button variant="ghost" size="sm" onClick={handleReply}>
          <Reply className="h-4 w-4 mr-1.5" /> Rispondi
        </Button>
        <Button variant="ghost" size="sm" onClick={handleForward}>
          <Forward className="h-4 w-4 mr-1.5" /> Inoltra
        </Button>
        <Button variant="ghost" size="sm" onClick={() => archiveMutation.mutate()} disabled={!threadId}>
          <Archive className="h-4 w-4 mr-1.5" /> Archivia
        </Button>
        <Button variant="ghost" size="sm" onClick={() => trashMutation.mutate()} disabled={!threadId}>
          <Trash2 className="h-4 w-4 mr-1.5" /> Cestino
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { if (confirm('Spostare nello spam e bloccare il mittente? I prossimi messaggi da questo indirizzo finiranno nel cestino.')) spamMutation.mutate(); }}
          disabled={!threadId || spamMutation.isPending}
          title="Segna come spam e blocca il mittente"
        >
          <Ban className="h-4 w-4 mr-1.5" /> Spam
        </Button>
        {threadId && <SnoozePicker onSnooze={(u) => snoozeMutation.mutate(u)} />}
        <Button variant="ghost" size="sm" onClick={handleRemindMe} disabled={!threadId}>
          <Bell className="h-4 w-4 mr-1.5" /> Promemoria
        </Button>
        {threadId && (
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button variant="ghost" size="sm">
                <Tag className="h-4 w-4 mr-1.5" /> Etichette
              </Button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content sideOffset={4} align="start" className="z-50 w-56 rounded-md border bg-popover shadow-md p-1">
                {catalog.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-2 py-2">
                    Nessuna etichetta. Creale dalla barra laterale.
                  </p>
                ) : (
                  catalog.map((l) => {
                    const assigned = threadLabels.includes(l.name);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => toggleLabel(l.name)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent text-left"
                      >
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                        <span className="flex-1 truncate">{l.name}</span>
                        {assigned && <Check className="h-3.5 w-3.5 text-primary" />}
                      </button>
                    );
                  })
                )}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => starMutation.mutate(!isStarred)}>
            <Star className={`h-4 w-4 ${isStarred ? 'text-amber-500 fill-amber-500' : ''}`} />
          </Button>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
          <div>
            <h1 className="text-2xl font-bold mb-3">{message.subject || '(Nessun oggetto)'}</h1>
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{fromName}</div>
                <div className="text-muted-foreground">{fromEmail}</div>
              </div>
              <div className="text-muted-foreground text-xs">{dateStr}</div>
            </div>
            {threadLabels.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {threadLabels.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full border px-2 py-0.5"
                    style={{ borderColor: colorByName[name] ?? '#94a3b8' }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorByName[name] ?? '#94a3b8' }} />
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {security && (
            <AvvisoMessaggio
              avviso={security}
              occupato={spamMutation.isPending || sicuraMutation.isPending}
              onSpam={() => spamMutation.mutate()}
              onSicura={() => sicuraMutation.mutate()}
            />
          )}

          {threadData?.thread && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <Sparkles className="h-4 w-4" />
                  Analisi AI
                </div>
                {!summary && (
                  <Button variant="ghost" size="sm" onClick={handleSummarize} disabled={generatingSummary}>
                    {generatingSummary && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    Genera riassunto
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {threadData.thread.ai_category && (
                  <Badge variant="secondary">{threadData.thread.ai_category}</Badge>
                )}
                {threadData.thread.ai_urgency && (
                  <Badge variant={threadData.thread.ai_urgency === 'critical' ? 'destructive' : 'default'}>
                    Urgenza: {threadData.thread.ai_urgency}
                  </Badge>
                )}
                {threadData.thread.snoozed_until && (
                  <Badge variant="secondary">
                    <ClockIcon className="h-3 w-3 mr-0.5" />
                    Snooze fino a {format(new Date(threadData.thread.snoozed_until), 'PP HH:mm', { locale: it })}
                  </Badge>
                )}
              </div>
              {summary && <p className="text-sm text-foreground/90 leading-relaxed">{summary}</p>}
              {actionItems.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">Azioni suggerite</div>
                  {actionItems.map((item, i) => (
                    <div key={i} className="text-sm flex items-start gap-2">
                      <span className="text-primary">→</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Allegati */}
          {message.has_attachments && attsData?.attachments && attsData.attachments.length > 0 && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Paperclip className="h-3 w-3" />
                Allegati ({attsData.attachments.length})
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attsData.attachments.map((a) => (
                  <AttachmentChip key={a.id} att={a} />
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-6">
            {message.body_html ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {message.body_text}
              </pre>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AttachmentChip({ att }: { att: AttachmentItem }) {
  async function handleDownload() {
    const access = tokenStore.getAccess();
    const url = `${API_URL.replace(/\/api\/v1$/, '')}${attachmentsApi.downloadUrl(att.id)}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${access}` } });
      if (!res.ok) throw new Error('Download fallito');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = att.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download fallito');
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      className="flex items-center gap-2 p-2 rounded-md border bg-background hover:bg-accent text-left"
    >
      <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{att.filename}</div>
        <div className="text-[11px] text-muted-foreground">
          {att.contentType ?? 'file'} - {formatBytes(att.sizeBytes)}
        </div>
      </div>
      <Download className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
